import { PieChart, Card, Skeleton, EmptyState } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

const PAYMENT_COLORS: Record<string, string> = {
  CASH: '#6366f1',
  UPI: '#10b981',
  CARD: '#f59e0b',
  WALLET: '#ef4444',
  SPLIT: '#8b5cf6',
};

interface PaymentModeWidgetProps {
  breakdown: Record<string, number> | undefined;
  loading?: boolean;
}

export function PaymentModeWidget({ breakdown, loading }: PaymentModeWidgetProps) {
  const currency = useTenantCurrency();

  if (loading) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <Skeleton variant="text" width="50%" height={16} />
        <div className="mt-3">
          <Skeleton variant="rect" height={140} />
        </div>
      </Card>
    );
  }

  if (!breakdown) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Payment Mode Split
          </p>
        </div>
        <EmptyState
          variant="inline"
          title="No payment data"
          description="Payments will appear here once bills are completed"
        />
      </Card>
    );
  }

  const data = Object.entries(breakdown)
    .filter(([, v]) => v > 0)
    .map(([label, value]) => ({
      label,
      value,
      color: PAYMENT_COLORS[label] ?? '#9ca3af',
    }));

  if (data.length === 0) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Payment Mode Split
          </p>
        </div>
        <EmptyState
          variant="inline"
          title="No payments today"
          description="Payment breakdown appears after first sale"
        />
      </Card>
    );
  }

  // The breakdown is in minor units: the slice tooltips need the tenant formatter.
  return <PieChart data={data} title="Payment Mode Split" donut valueFormatter={(v) => formatPaise(v, currency)} className="shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg" />;
}
